import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { formatDate } from './weekendUtils';
import { DialogHeader, SectionLabel } from './WeekendDialogParts';

/** Log hours against one approved weekend date; the manager approves them afterwards. */
const WeekendTaskDialog = ({
  open,
  onClose,
  onSubmit,
  date,
  projectName,
  submitting,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState('');

  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setHours('');
    }
  }, [open, date]);

  const hoursNumber = Number(hours);
  const hoursValid = hours !== '' && hoursNumber > 0;
  const canSubmit =
    hoursValid &&
    title.trim() !== '' &&
    description.trim() !== '' &&
    !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogHeader
        icon={<AssignmentOutlinedIcon />}
        title="Add weekend task"
        subtitle={`${date ? formatDate(date) : ''}${projectName ? ` · ${projectName}` : ''}`}
      />
      <DialogContent sx={{ pt: '20px !important' }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Box>
            <SectionLabel>Task</SectionLabel>
            <TextField
              fullWidth
              required
              size="small"
              label="Task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Box>
          <Box>
            <SectionLabel>Hours worked</SectionLabel>
            <TextField
              fullWidth
              required
              size="small"
              type="number"
              label="Hours"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              inputProps={{ min: 0, step: 0.25 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">hrs</InputAdornment>
                ),
              }}
              error={hours !== '' && !hoursValid}
              helperText={
                hours !== '' && !hoursValid
                  ? 'Enter more than 0 hours'
                  : 'Decimals allowed, for example 7.5'
              }
            />
          </Box>
          <Box>
            <SectionLabel>Description</SectionLabel>
            <TextField
              fullWidth
              required
              size="small"
              label="What did you work on?"
              value={description}
              multiline
              minRows={4}
              maxRows={4}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Hours count only after your project manager approves this task.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              title: title.trim(),
              description: description.trim(),
              hours: hoursNumber,
            })
          }
        >
          Send for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeekendTaskDialog;
